import {
  AsyncActionState,
  asyncActionStateMatchers,
  invoke,
  setupSlice,
} from '@owl-frontend/redux';
import {
  fetchArtifactTemplates,
  fetchDossierArtifacts,
  generateArtifact,
} from '../../api/artifacts/client';
import type { Artifact, ArtifactTemplate } from '../../api/artifacts/interface';

export interface ArtifactsState {
  byDossierId: {
    [dossier_id: string]: Artifact[];
  };
  statusByDossierId: {
    [dossier_id: string]: string;
  };
  templatesByDossierId: {
    [dossier_id: string]: ArtifactTemplate[];
  };
  actions: {
    fetchArtifacts: AsyncActionState<Artifact[]>;
    fetchArtifactTemplates: AsyncActionState<ArtifactTemplate[]>;
    generateArtifact: AsyncActionState<Artifact>;
  };
}

const initialState: ArtifactsState = {
  byDossierId: {},
  statusByDossierId: {},
  templatesByDossierId: {},
  actions: {
    fetchArtifacts: { status: 'uninitialized' },
    fetchArtifactTemplates: { status: 'uninitialized' },
    generateArtifact: { status: 'uninitialized' },
  },
};

const slice = setupSlice('artifacts', initialState)
  .addAsyncSagas({
    fetchArtifacts: invoke(fetchDossierArtifacts),
    fetchArtifactTemplates: invoke(fetchArtifactTemplates),
    generateArtifact: invoke(generateArtifact),
  })
  .addReducers({
    'fetchArtifacts/pending': (state, action) => {
      state.statusByDossierId[action.meta.arg] = 'pending';
    },
    'fetchArtifacts/fulfilled': (state, action) => {
      const dossierId = action.meta.arg;
      const existing = state.byDossierId[dossierId] ?? [];
      // Preserve in-progress streaming placeholders (created_at === null)
      // so a concurrent EventProcessed refetch doesn't evict them.
      const streaming = existing.filter((a) => a.created_at === null);
      state.byDossierId[dossierId] = [...action.payload, ...streaming];
      state.statusByDossierId[dossierId] = 'fulfilled';
    },
    'fetchArtifacts/rejected': (state, action) => {
      state.statusByDossierId[action.meta.arg] = 'rejected';
    },
    'fetchArtifactTemplates/fulfilled': (state, action) => {
      state.templatesByDossierId[action.meta.arg] = action.payload;
    },
    'generateArtifact/fulfilled': (state, action) => {
      const list = state.byDossierId[action.meta.arg.dossierId];
      if (list) {
        list.push(action.payload);
      }
    },
    injectArtifact: (
      state,
      action: {
        payload: {
          dossierId: string;
          artifact: Partial<Artifact> & { artifact_id: string };
        };
      }
    ) => {
      const { dossierId, artifact } = action.payload;
      if (!state.byDossierId[dossierId]) {
        state.byDossierId[dossierId] = [];
        state.statusByDossierId[dossierId] = 'fulfilled';
      }
      const list = state.byDossierId[dossierId];
      const idx = list.findIndex((a) => a.artifact_id === artifact.artifact_id);
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...artifact };
      } else {
        list.push(artifact as Artifact);
      }
    },
  });

export const actions = slice.actions;
export const removeArtifactAction = (
  dossierId: string,
  artifactId: string
) => ({
  type: 'artifacts/removeArtifact' as const,
  payload: { dossierId, artifactId },
});
export const injectArtifactAction = (
  dossierId: string,
  artifact: Partial<Artifact> & { artifact_id: string }
) => ({
  type: 'artifacts/injectArtifact' as const,
  payload: { dossierId, artifact },
});
export default slice
  .addReducers({
    removeArtifact: (state, action) => {
      const { dossierId, artifactId } = (action as any).payload as {
        dossierId: string;
        artifactId: string;
      };
      const list = state.byDossierId[dossierId];
      if (list) {
        state.byDossierId[dossierId] = list.filter(
          (a) => a.artifact_id !== artifactId
        );
      }
    },
  })
  .addExtra(asyncActionStateMatchers(actions).all());
